import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Image } from '@chakra-ui/react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import { queryClient } from '../lib/query-client';
import {
  AppLayout,
  type NavItem,
  BrandKey,
  resolveBrandKey,
} from '@themes/aegix';
import { getBrandKeyFromAuthCookie } from '../utils/brandKeyFromJwt';
import {
  HiBuildingOffice2,
  HiUserCircle,
  HiArrowsPointingIn,
  HiBolt,
  HiChartBar,
  HiUserGroup,
} from 'react-icons/hi2';

// ADR-118 — auth state lives in `@aegix/auth-*`.
// `AuthProvider` + `OrganizationProvider` come from the libs; every
// consumer reads the narrow lib hooks (`useUser`, `useIsAuthenticated`,
// `useAuthActions`, `useHasPermission`, `useActiveOrganization`,
// `useIsVendorMasqueradeAvailable`) directly — no compat shim.
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthProvider as LibAuthProvider, useUser } from '@aegix/auth-identity';
import { useHasPermission } from '@aegix/auth-permissions';
import { OrganizationProvider } from '@aegix/auth-organization';
import {
  smsStorage,
  smsLoginAdapter,
  smsRefreshAdapter,
  type SMSUser,
} from '../lib/sms-auth';
import { SocketProvider } from '../contexts/SocketContext';
import { SocketMessageRouter } from '../contexts/SocketMessageRouter';
import { ReunificationJoinPromptProvider } from '../contexts/ReunificationJoinPromptContext';
import { ReunificationJoinPromptUI } from '../components/reunification/ReunificationJoinPromptUI';
import { BrandRedirectGate } from '../components/BrandRedirectGate';
import { KioskProvider } from '../contexts/KioskContext';
import { SearchProvider } from '../contexts/SearchContext';
import { GlobalSearchDrawer } from '../components/ui/GlobalSearchDrawer';
import { PrivateRoute } from '../components/PrivateRoute';
import { DeliveryMetricsRoute } from '../components/DeliveryMetricsRoute';
import { useVisitorPermissions } from '../hooks/useVisitorPermissions';
import { KioskGuard } from '../components/kiosk';
import { VendorOrgSelector } from '../components/VendorOrgSelector';
import { FeatureGate } from '../components/FeatureGate';
import { useFeature } from '../hooks/useFeature';
import { FeatureCode } from '../types/features';
import { Toaster } from '../components/ui/toaster';
import { PushNotificationsBootstrap } from '../push/PushNotificationsBootstrap';
import { Provider } from '../components/ui/provider';
import { AriaLiveProvider } from '../components/ui/AriaLiveRegion';
import { Login, OAuthSuccess } from '../pages/auth';
import {
  Dashboard,
  Sites,
  Account,
  Organizations,
  AdminDocs,
  AdminDocsLegacyRedirect,
  Reunification,
  Events,
  VisitorMgmt,
  NotFound,
} from '../pages';
import {
  KioskWelcome,
  KioskCheckIn,
  KioskResult,
  KioskCheckOut,
} from '../pages/kiosk';
import aegixLogo from '../assets/aegix-logo.svg';
import aegixLogoWhite from '../assets/aegix-logo-white.svg';
import safeLogo from '../assets/safe-logo.svg';
import safeLogoWhite from '../assets/safe-logo-white.svg';
import { useColorMode } from '../components/ui/color-mode';

// ADR 115 Phase 5: brand via the layered resolver, not the
// dropped getBrandMode() facade. Matches the Provider's resolution.
const isSafe =
  resolveBrandKey({
    jwtClaim: getBrandKeyFromAuthCookie(),
    hostname: typeof window !== 'undefined' ? window.location.hostname : undefined,
    supportedBrands: [BrandKey.Aegix, BrandKey.SafePlus],
  }) === BrandKey.SafePlus;

function Logo() {
  const { colorMode } = useColorMode();
  const logoSrc = isSafe
    ? colorMode === 'dark'
      ? safeLogoWhite
      : safeLogo
    : colorMode === 'dark'
      ? aegixLogoWhite
      : aegixLogo;
  return <Image src={logoSrc} alt={isSafe ? 'SAFE+' : 'AEGIX'} w="100%" />;
}

const baseNavItems: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: HiChartBar, 'data-e2e': 'dashboard' },
  { label: 'Sites', path: '/sites', icon: HiBuildingOffice2, 'data-e2e': 'sites' },
];

const reunificationNavItems: NavItem[] = [
  { label: 'Reunify', path: '/reunification', icon: HiArrowsPointingIn, 'data-e2e': 'reunification' },
  { label: 'Events', path: '/events', icon: HiBolt, 'data-e2e': 'events' },
];

const visitorNavItems: NavItem[] = [
  { label: 'Host', path: '/visitor-mgmt', icon: HiUserGroup, 'data-e2e': 'visitorMgmt' },
];

// Standalone admin nav entry for the delivery-metrics page.
// Gated on the same notifications-admin permission as the Dashboard tab;
// only rendered for admins (see AuthenticatedLayout).
const DELIVERY_METRICS_PERMISSION = 'notifications.admin_fcm_device_token';

const deliveryMetricsNavItems: NavItem[] = [
  {
    label: 'Delivery Metrics',
    path: '/account/notifications/delivery-metrics',
    icon: HiChartBar,
    'data-e2e': 'delivery-metrics',
  },
];

const trailingNavItems: NavItem[] = [
  {
    label: 'Account',
    path: '/account',
    icon: HiUserCircle,
    hasDividerAbove: true,
    'data-e2e': 'admin',
  },
];

/**
 * Param-preserving redirect from `/admin/*` to `/account/*` (ADR-114 Phase 1).
 * Used when the user lands on the legacy Admin URL — preserves `?tab=`,
 * `?subtab=`, and any other query string so deep links keep working.
 */
function AdminToAccountRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: location.pathname.replace(/^\/admin/, '/account'),
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

/** Redirect `/admin/docs/:slug` to `/account/docs/:slug`. */
function AdminDocsSlugRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return (
    <Navigate to={slug ? `/account/docs/${slug}` : '/account/docs'} replace />
  );
}

/**
 * Redirect the retired top-level `/operating` surface into its new home,
 * Sites → Plans. Maps the old operating sub-tab to the Plans
 * sub-tab (`?tab=hours|routing|terminology` → `?tab=plans&subtab=…`).
 */
function OperatingToPlansRedirect() {
  const location = useLocation();
  const tab = new URLSearchParams(location.search).get('tab');
  const subtab =
    tab && ['hours', 'routing', 'terminology'].includes(tab)
      ? `&subtab=${tab}`
      : '';
  return <Navigate to={`/sites?tab=plans${subtab}`} replace />;
}

function UserLanguageSync() {
  const { i18n } = useTranslation();
  const user = useUser<SMSUser>();
  const syncedUserLangRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const userLang = user?.language;
    if (userLang && userLang !== syncedUserLangRef.current) {
      syncedUserLangRef.current = userLang;
      const cleanLang = userLang.split('-')[0];
      if (i18n.language !== cleanLang) {
        void i18n.changeLanguage(cleanLang);
      }
    }
  }, [user?.language, i18n]);

  return null;
}

function AuthenticatedLayout() {
  const { t } = useTranslation();
  const isReunificationEnabled = useFeature(FeatureCode.REUNIFICATION);
  const isVisitorManagementEnabled = useFeature(FeatureCode.VISITOR_MANAGEMENT);
  const { canObserve: canObserveVisitorMgmt, isLoading: vmPermsLoading } = useVisitorPermissions({
    enabled: isVisitorManagementEnabled,
  });
  const canViewDeliveryMetrics = useHasPermission(DELIVERY_METRICS_PERMISSION);

  const navItems = useMemo(() => {
    const rawNavItems = [
      ...baseNavItems,
      ...(isVisitorManagementEnabled && !vmPermsLoading && canObserveVisitorMgmt ? visitorNavItems : []),
      ...(isReunificationEnabled ? reunificationNavItems : []),
      ...(canViewDeliveryMetrics ? deliveryMetricsNavItems : []),
      ...trailingNavItems,
    ];

    const NAV_KEY_MAP: Record<string, string> = {
      dashboard: 'dashboard',
      sites: 'sites',
      reunification: 'reunification',
      events: 'events',
      visitorMgmt: 'visitorMgmt',
      'delivery-metrics': 'deliveryMetrics',
      admin: 'account',
      account: 'account',
    };

    return rawNavItems.map((item) => {
      const key =
        NAV_KEY_MAP[item['data-e2e'] || ''] ||
        item.path.replace('/', '') ||
        'dashboard';
      return {
        ...item,
        label: t(`nav.${key}`, item.label),
      };
    });
  }, [
    t,
    isVisitorManagementEnabled,
    vmPermsLoading,
    canObserveVisitorMgmt,
    isReunificationEnabled,
    canViewDeliveryMetrics,
  ]);

  return (
    <>
      <AppLayout
        navItems={navItems}
        logo={<Logo />}
        userMenuLocation="header"
        headerSlot={<VendorOrgSelector />}
      />
      <GlobalSearchDrawer />
    </>
  );
}

const LOCALE_STORAGE_KEYS = ['i18nextLng'];

// Storage adapter created once at module init so the
// React tree gets a stable reference. The adapter bridges
// auth-core's logical keys to SMS's legacy localStorage layout. The SAME
// `smsStorage` instance backs `@tiaki/http-client` (lib/http-client.ts).
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider>
        <AriaLiveProvider>
          <LibAuthProvider<SMSUser>
            loginAdapter={smsLoginAdapter}
            refreshAdapter={smsRefreshAdapter}
            storage={smsStorage}
            localeStorageKeys={LOCALE_STORAGE_KEYS}
            // ADR-118 Phase 2: the backend issues the access session
            // cookie under the per-app name for client
            // `sms` — reconcile()'s cookie-presence check must match it.
            cookieName="jwt-auth-sms"
          >
            <UserLanguageSync />
            <OrganizationProvider storage={smsStorage}>
              <SocketProvider>
                <ReunificationJoinPromptProvider>
                  <SocketMessageRouter>
                    <SearchProvider>
                      <KioskProvider>
                        <BrowserRouter>
                          {/* Web push lifecycle (register on
                            login, 7-day refresh, token_refresh pushes,
                            foreground toasts, notification-click nav).
                            Inside KioskProvider (kiosk gate) + Router
                            (click navigation). */}
                          <PushNotificationsBootstrap />
                          <ReunificationJoinPromptUI />
                          {/* ADR 115 Phase 4 — honors redirect_url:
                            immediate nav on login, interstitial on refresh. */}
                          <BrandRedirectGate />
                          <Routes>
                            {/* Public routes */}
                            <Route path="/login" element={<Login />} />
                            <Route path="/oauth-success" element={<OAuthSuccess />} />
                            <Route
                              path="/oauth-success/:jwt"
                              element={<OAuthSuccess />}
                            />

                            {/* Kiosk routes — authenticated but outside normal layout */}
                            <Route
                              path="/kiosk"
                              element={
                                <PrivateRoute>
                                  <KioskWelcome />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/kiosk/check-in"
                              element={
                                <PrivateRoute>
                                  <KioskCheckIn />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/kiosk/result"
                              element={
                                <PrivateRoute>
                                  <KioskResult />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/kiosk/check-out"
                              element={
                                <PrivateRoute>
                                  <KioskCheckOut />
                                </PrivateRoute>
                              }
                            />

                            {/* Protected routes — guarded by KioskGuard */}
                            <Route
                              path="/"
                              element={
                                <PrivateRoute>
                                  <KioskGuard>
                                    <AuthenticatedLayout />
                                  </KioskGuard>
                                </PrivateRoute>
                              }
                            >
                              <Route index element={<Dashboard />} />
                              <Route path="sites" element={<Sites />} />
                              {/* Operating config lives under Sites → Plans;
                                the old path stays as a redirect so
                                bookmarks/deep-links resolve. */}
                              <Route
                                path="operating"
                                element={<OperatingToPlansRedirect />}
                              />
                              <Route path="account" element={<Account />} />
                              <Route
                                path="account/organizations"
                                element={<Organizations />}
                              />
                              <Route path="account/docs" element={<AdminDocs />} />
                              <Route
                                path="account/docs/:slug"
                                element={<AdminDocs />}
                              />
                              {/* Standalone, bookmarkable admin page for
                                notification delivery metrics (ADR-041
                                Phase 5). Admin-gated inside the
                                element; the embedded Dashboard tab stays. */}
                              <Route
                                path="account/notifications/delivery-metrics"
                                element={<DeliveryMetricsRoute />}
                              />
                              {/* Legacy redirects — ADR-114 Phase 1. */}
                              <Route path="admin" element={<AdminToAccountRedirect />} />
                              <Route
                                path="admin/notifications/delivery-metrics"
                                element={
                                  <Navigate
                                    to="/account/notifications/delivery-metrics"
                                    replace
                                  />
                                }
                              />
                              <Route
                                path="admin/docs"
                                element={<Navigate to="/account/docs" replace />}
                              />
                              <Route
                                path="admin/docs/:slug"
                                element={<AdminDocsSlugRedirect />}
                              />
                              <Route
                                path="integrations"
                                element={
                                  <Navigate to="/account?tab=integrations" replace />
                                }
                              />
                              <Route
                                path="docs/admin-guides"
                                element={<AdminDocsLegacyRedirect />}
                              />
                              <Route
                                path="docs/admin-guides/:slug"
                                element={<AdminDocsLegacyRedirect />}
                              />
                              <Route
                                path="visitor-mgmt"
                                element={
                                  <FeatureGate code={FeatureCode.VISITOR_MANAGEMENT}>
                                    <VisitorMgmt />
                                  </FeatureGate>
                                }
                              />
                              <Route
                                path="reunification"
                                element={
                                  <FeatureGate code={FeatureCode.REUNIFICATION}>
                                    <Reunification />
                                  </FeatureGate>
                                }
                              />
                              <Route
                                path="events"
                                element={
                                  <FeatureGate code={FeatureCode.REUNIFICATION}>
                                    <Events />
                                  </FeatureGate>
                                }
                              />
                            </Route>

                            {/* 404 */}
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </BrowserRouter>
                        <Toaster />
                      </KioskProvider>
                    </SearchProvider>
                  </SocketMessageRouter>
                </ReunificationJoinPromptProvider>
              </SocketProvider>
            </OrganizationProvider>
          </LibAuthProvider>
        </AriaLiveProvider>
      </Provider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
